import Bugsnag, { type Breadcrumb, type Event } from "@bugsnag/js";
import { Capacitor } from "@capacitor/core";
import { STAGE } from "./globals";
import { appVersion } from "./utils/app-version";

export function appType(): "iOS" | "Android" | "Browser" {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") {
    return "iOS";
  } else if (platform === "android") {
    return "Android";
  }
  return "Browser";
}

export async function startBugsnag(): Promise<void> {
  Bugsnag.start({
    apiKey: Capacitor.isNativePlatform()
      ? "231db42287ec10ab6030f410210c3de2" // Docket PWA Capacitor  project
      : "d521ee05a3871fc096427c05e2b6ceac", // Docket PWA project
    appVersion: await appVersion(),
    appType: appType(),
    redactedKeys: [/password/i, /token/i, /name/i, /dob/i, /phone/i, /title/i],
    releaseStage: STAGE,
    onBreadcrumb: stripBreadcrumbUrls,
    onError: stripRequestUrl,
  });
}

// URLs carry one-time sign-in codes and tokens: Apple's answer to Android, an emailed link's
// oobCode, a state login's code. redactedKeys only match metadata keys, so Bugsnag gets URLs
// without their query and fragment.

/** A URL, or a request breadcrumb's "GET https://…", without its query and fragment. */
export function withoutQuery(url: string): string {
  return url.replace(/[?#].*$/s, "");
}

/** Navigation breadcrumbs' from and to, and requests' URLs. */
export function stripBreadcrumbUrls({ metadata }: Breadcrumb): void {
  const { from, to, request } = metadata;
  if (typeof from === "string") {
    metadata.from = withoutQuery(from);
  }
  if (typeof to === "string") {
    metadata.to = withoutQuery(to);
  }
  if (typeof request === "string") {
    metadata.request = withoutQuery(request);
  }
}

/** The page the error happened on. */
export function stripRequestUrl(event: Event): void {
  if (event.request.url) {
    event.request.url = withoutQuery(event.request.url);
  }
}
